import type {InspectionScheduleResponse} from 'src/api/ProactApiClient';
import {
  InspectionScheduleCadenceEnum,
  InspectionScheduleHealthIssueEnum,
  InspectionStateEnum,
} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import {formatUtcShortDate} from 'src/shared/utils/formatters';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';

/**
 * Presentation helpers shared by every inspection-schedule surface.
 * @registry Inspection schedule cadence label, site-local date, health pill props and duplicate detection.
 */
export const cadenceLabel = (schedule: {
  cadence: InspectionScheduleCadenceEnum;
  intervalDays?: number;
}): string => {
  switch (schedule.cadence) {
    case InspectionScheduleCadenceEnum.Quarterly:
      return 'Quarterly';
    case InspectionScheduleCadenceEnum.Annual:
      return 'Annual';
    case InspectionScheduleCadenceEnum.Custom:
      return schedule.intervalDays
        ? `Every ${schedule.intervalDays} days`
        : 'Custom';
    default:
      return 'Unknown';
  }
};

/** Maps `*SelectOptions` rows to Mantine `{value, label}` data. */
export const selectOptionsToData = (
  options: {id: number; label?: string}[] | undefined
): {value: string; label: string}[] =>
  (options ?? []).map(o => ({value: String(o.id), label: o.label ?? String(o.id)}));

// Renders the site-local calendar date of a UTC instant (the feature premise is
// site-local scheduling; the viewer's browser zone must not shift the day).
export const formatSiteLocalDate = (
  utc: Date | string | undefined,
  timeZone?: string
): string => formatUtcShortDate(utc, '—', timeZone || 'UTC');

// Labels stay factual: only a missing time zone actually stops generation, so
// no label claims that generation has stopped.
export const healthIssuePill = (
  issue: InspectionScheduleHealthIssueEnum
): PillProps => {
  switch (issue) {
    case InspectionScheduleHealthIssueEnum.SiteTimeZoneMissing:
      return {text: 'No time zone', color: 'red'};
    case InspectionScheduleHealthIssueEnum.SiteInactive:
      return {text: 'Site inactive', color: 'gray'};
    case InspectionScheduleHealthIssueEnum.InspectionTypeInactive:
      return {text: 'Type inactive', color: 'gray'};
    default:
      return {text: 'Unknown issue', color: 'gray'};
  }
};

export const healthIssueTooltip = (
  issue: InspectionScheduleHealthIssueEnum
): string | undefined =>
  issue === InspectionScheduleHealthIssueEnum.SiteTimeZoneMissing
    ? "Due dates can't be computed without a site time zone, so no inspections generate."
    : undefined;

export const isScheduleOverdue = (
  schedule: Pick<
    InspectionScheduleResponse,
    'latestInspectionState' | 'latestInspectionDueAt'
  >,
  now: Date = new Date()
): boolean => {
  if (!schedule.latestInspectionDueAt) return false;
  if (schedule.latestInspectionState === InspectionStateEnum.Completed) {
    return false;
  }
  return parseUtcDate(schedule.latestInspectionDueAt).getTime() < now.getTime();
};

export interface ScheduleHealthPill {
  pill: PillProps;
  /** Explains the consequence of the issue; rendered as a `Tooltip` at the call site. */
  tooltip?: string;
}

// Configuration issues only. Overdue is a cycle state, not a health issue, and
// belongs beside the current-cycle pill.
export const scheduleHealthPills = (
  schedule: InspectionScheduleResponse
): ScheduleHealthPill[] =>
  (schedule.healthIssues ?? []).map(issue => ({
    pill: healthIssuePill(issue),
    tooltip: healthIssueTooltip(issue),
  }));

export const OVERDUE_PILL: PillProps = {text: 'Overdue', color: 'red'};

export interface DuplicateScheduleCandidate {
  id?: number;
  siteId: number;
  inspectionTypeId: number;
  cadence: InspectionScheduleCadenceEnum;
  intervalDays?: number;
}

/** Active schedules on the same site + type + cadence (+ interval for Custom), excluding the edited row. */
export const findDuplicateSchedules = (
  schedules: InspectionScheduleResponse[],
  candidate: DuplicateScheduleCandidate
): InspectionScheduleResponse[] =>
  schedules.filter(
    schedule =>
      schedule.active &&
      schedule.id !== candidate.id &&
      schedule.siteId === candidate.siteId &&
      schedule.inspectionTypeId === candidate.inspectionTypeId &&
      schedule.cadence === candidate.cadence &&
      (candidate.cadence !== InspectionScheduleCadenceEnum.Custom ||
        (schedule.intervalDays ?? null) === (candidate.intervalDays ?? null))
  );
